import { z } from 'zod';

/**
 * `forge.v1`: a code forge (GitHub, GitLab): repositories, pull requests with their checks and
 * reviews, issues, and the events its webhooks deliver. Repositories are named `owner/name` on the
 * provider's host; a provider declares the hosts it serves (`provides[].hosts`, and the servers
 * of its `servers` connection). A user's forges are all active at once: the core calls the one
 * serving a repository's host, with that host in `FORGE_HOST_HEADER`.
 */
/** The host a call is for (`github.com`, a GitHub Enterprise server); absent for a listing. */
export const FORGE_HOST_HEADER = 'x-crowdex-forge-host';

/** The host a provider call is for, lower-cased, or null when the core named none. */
export function forgeHostOfRequest(request: Request): string | null {
  const host = request.headers.get(FORGE_HOST_HEADER)?.trim().toLowerCase();
  return host ? host : null;
}

const text = (max: number) => z.string().max(max);
const timestamp = z.string().max(64).nullable();

export const forgeRepositoryRefSchema = z
  .string()
  .min(3)
  .max(256)
  .regex(/^[A-Za-z0-9_.-]+(?:\/[A-Za-z0-9_.-]+)+$/, 'Repositories are "owner/name".');

export const forgeRepositorySchema = z
  .object({
    host: text(256),
    fullName: forgeRepositoryRefSchema,
    url: z.url().max(2048),
    cloneUrl: z.url().max(2048),
    defaultBranch: text(256),
    private: z.boolean(),
    description: text(2000).nullable(),
  })
  .strict();
export type ForgeRepository = z.infer<typeof forgeRepositorySchema>;

export const forgeActorSchema = z
  .object({ login: text(256), avatarUrl: z.string().max(2048).nullable() })
  .strict();

const reactionSchema = z.object({ emoji: text(64), count: z.number().int().min(0) }).strict();

export const forgePullRequestStateSchema = z.enum(['open', 'draft', 'merged', 'closed']);

/** A pull request as lists show it. */
export const forgePullRequestSummarySchema = z
  .object({
    repository: forgeRepositoryRefSchema,
    number: z.number().int().positive(),
    url: z.url().max(2048),
    title: text(1000),
    state: forgePullRequestStateSchema,
    baseBranch: text(256),
    headBranch: text(256),
    headSha: text(64),
    author: forgeActorSchema,
    updatedAt: timestamp,
  })
  .strict();
export type ForgePullRequestSummary = z.infer<typeof forgePullRequestSummarySchema>;

export const forgeCheckSchema = z
  .object({
    id: text(256),
    name: text(500),
    status: z.enum(['queued', 'in_progress', 'completed', 'unknown']),
    /** `success`, `failure`, `neutral`, `cancelled`, `skipped`, `timed_out`, `action_required`. */
    conclusion: text(64).nullable(),
    detailsUrl: z.string().max(2048).nullable(),
    summary: text(10_000).nullable(),
    startedAt: timestamp,
    completedAt: timestamp,
  })
  .strict();
export type ForgeCheck = z.infer<typeof forgeCheckSchema>;

export const forgeCommentSchema = z
  .object({
    id: text(256),
    /** `comment` (conversation), `review` (a review's body), `inline` (on a line). */
    kind: z.enum(['comment', 'review', 'inline']),
    author: forgeActorSchema,
    body: text(65_536),
    state: z.enum(['approved', 'changes_requested', 'commented', 'dismissed']).nullable(),
    path: text(1024).nullable(),
    line: z.number().int().nullable(),
    diffHunk: text(65_536).nullable(),
    url: z.string().max(2048).nullable(),
    createdAt: timestamp,
    reactions: z.array(reactionSchema).max(16),
    /** The comment an inline comment answers. */
    replyToId: text(256).nullable(),
  })
  .strict();
export type ForgeComment = z.infer<typeof forgeCommentSchema>;

export const forgeReviewThreadSchema = z
  .object({
    id: text(256),
    path: text(1024),
    line: z.number().int().nullable(),
    startLine: z.number().int().nullable(),
    side: z.enum(['left', 'right']).nullable(),
    isResolved: z.boolean(),
    isOutdated: z.boolean(),
    viewerCanResolve: z.boolean(),
    viewerCanUnresolve: z.boolean(),
    comments: z.array(forgeCommentSchema).max(500),
  })
  .strict();
export type ForgeReviewThread = z.infer<typeof forgeReviewThreadSchema>;

/** A pull request in full, as its panel shows it. */
export const forgePullRequestSchema = forgePullRequestSummarySchema
  .extend({
    body: text(262_144),
    baseSha: text(64),
    mergeable: z.enum(['mergeable', 'conflicting', 'unknown']),
    autoMergeEnabled: z.boolean(),
    reviewDecision: z.enum(['approved', 'changes_requested', 'review_required', 'unknown']),
    additions: z.number().int().min(0),
    deletions: z.number().int().min(0),
    changedFiles: z.number().int().min(0),
    commits: z
      .array(
        z
          .object({
            oid: text(64),
            headline: text(1000),
            authoredAt: timestamp,
            author: forgeActorSchema,
          })
          .strict(),
      )
      .max(250),
    labels: z.array(text(256)).max(100),
    requestedReviewers: z.array(forgeActorSchema).max(100),
    reactions: z.array(reactionSchema).max(16),
  })
  .strict();
export type ForgePullRequest = z.infer<typeof forgePullRequestSchema>;

export const forgeIssueSchema = z
  .object({
    repository: forgeRepositoryRefSchema,
    number: z.number().int().positive(),
    url: z.url().max(2048),
    title: text(1000),
    body: text(262_144),
    state: z.enum(['open', 'closed']),
    author: forgeActorSchema,
    labels: z.array(text(256)).max(100),
    assignees: z.array(forgeActorSchema).max(100),
    comments: z.number().int().min(0),
    createdAt: timestamp,
    updatedAt: timestamp,
  })
  .strict();
export type ForgeIssue = z.infer<typeof forgeIssueSchema>;

/** What a webhook delivery says changed, for the core to route to the owning account. */
export const forgeEventSchema = z
  .object({
    kind: z.enum(['pull_request', 'check', 'review', 'comment', 'issue']),
    action: text(64),
    repository: forgeRepositoryRefSchema,
    number: z.number().int().positive().nullable(),
    /** The commit a check ran on. */
    sha: text(64).nullable(),
    /** Who the delivery is for on the provider's side (a GitHub App installation). */
    installation: text(256).nullable(),
  })
  .strict();
export type ForgeEvent = z.infer<typeof forgeEventSchema>;

const repository = { repository: forgeRepositoryRefSchema };
const pullRequest = { ...repository, number: z.number().int().positive() };
const list = {
  limit: z.number().int().min(1).max(100).default(30),
  cursor: text(512).optional(),
};
/** A page of a list and the cursor of the next one. */
const cursor = text(512).nullable();
const empty = z.object({}).strict();

export const forgeV1 = {
  'repository.resolve': {
    /** A git remote URL (`git@github.com:a/b.git`, `https://…`) or `owner/name`. */
    input: z.object({ remote: text(2048) }).strict(),
    output: z.object({ repository: forgeRepositorySchema.nullable() }).strict(),
  },
  'repositories.list': {
    input: z.object({ query: text(256).optional(), ...list }).strict(),
    output: z.object({ repositories: z.array(forgeRepositorySchema).max(100), cursor }).strict(),
  },
  'branches.list': {
    input: z.object({ ...repository, query: text(256).optional(), ...list }).strict(),
    output: z
      .object({
        branches: z
          .array(z.object({ name: text(256), protected: z.boolean(), sha: text(64) }).strict())
          .max(100),
        cursor,
      })
      .strict(),
  },
  'pullRequest.get': {
    input: z
      .object({
        ...repository,
        number: z.number().int().positive().optional(),
        /** The open pull request of this head branch. */
        head: text(256).optional(),
      })
      .strict()
      .refine((input) => (input.number === undefined) !== (input.head === undefined), {
        message: 'A pull request is named by its number or its head branch.',
      }),
    output: z.object({ pullRequest: forgePullRequestSchema.nullable() }).strict(),
  },
  'pullRequest.list': {
    input: z
      .object({
        ...repository,
        state: z.enum(['open', 'closed', 'all']).default('open'),
        head: text(256).optional(),
        ...list,
      })
      .strict(),
    output: z
      .object({ pullRequests: z.array(forgePullRequestSummarySchema).max(100), cursor })
      .strict(),
  },
  'pullRequest.create': {
    input: z
      .object({
        ...repository,
        base: text(256),
        head: text(256),
        title: text(1000).min(1),
        body: text(262_144).default(''),
        draft: z.boolean().default(false),
      })
      .strict(),
    output: z.object({ pullRequest: forgePullRequestSummarySchema }).strict(),
  },
  'pullRequest.update': {
    input: z
      .object({
        ...pullRequest,
        title: text(1000).min(1).optional(),
        body: text(262_144).optional(),
        draft: z.boolean().optional(),
        /** `open` reopens a closed pull request. */
        state: z.literal('open').optional(),
        reviewers: z.array(text(256)).max(50).optional(),
        autoMerge: z
          .union([z.literal(false), z.object({ method: z.enum(['merge', 'squash']) }).strict()])
          .optional(),
        /** Merges the base branch into the head branch. */
        updateBranch: z.literal(true).optional(),
        expectedHeadSha: text(64).optional(),
      })
      .strict(),
    output: z.object({ pullRequest: forgePullRequestSummarySchema }).strict(),
  },
  'pullRequest.merge': {
    input: z
      .object({
        ...pullRequest,
        method: z.enum(['merge', 'squash']),
        expectedHeadSha: text(64),
      })
      .strict(),
    output: z.object({ merged: z.boolean(), sha: text(64).nullable() }).strict(),
  },
  'pullRequest.close': {
    input: z.object(pullRequest).strict(),
    output: empty,
  },
  'checks.list': {
    input: z.object({ ...repository, ref: text(256) }).strict(),
    output: z.object({ checks: z.array(forgeCheckSchema).max(500) }).strict(),
  },
  'reviews.list': {
    input: z.object(pullRequest).strict(),
    output: z
      .object({
        reviews: z.array(forgeCommentSchema).max(500),
        threads: z.array(forgeReviewThreadSchema).max(500),
      })
      .strict(),
  },
  'reviews.create': {
    input: z
      .object({
        ...pullRequest,
        decision: z.enum(['approve', 'request_changes', 'comment']),
        body: text(65_536).default(''),
        expectedHeadSha: text(64).optional(),
      })
      .strict(),
    output: empty,
  },
  /** Resolves or reopens a review thread. */
  'reviews.resolve': {
    input: z.object({ ...pullRequest, threadId: text(256), resolved: z.boolean() }).strict(),
    output: empty,
  },
  'comments.list': {
    input: z.object(pullRequest).strict(),
    output: z.object({ comments: z.array(forgeCommentSchema).max(1000) }).strict(),
  },
  'comments.create': {
    input: z
      .object({
        ...pullRequest,
        body: text(65_536).min(1),
        /** A reply in this review thread. */
        threadId: text(256).optional(),
      })
      .strict(),
    output: z.object({ comment: forgeCommentSchema }).strict(),
  },
  /** Adds a reaction to a comment. */
  'comments.react': {
    input: z
      .object({
        ...pullRequest,
        commentId: text(256),
        reaction: z.enum(['+1', '-1', 'laugh', 'hooray', 'confused', 'heart', 'rocket', 'eyes']),
      })
      .strict(),
    output: empty,
  },
  'issues.list': {
    input: z
      .object({
        ...repository,
        state: z.enum(['open', 'closed', 'all']).default('open'),
        query: text(256).optional(),
        ...list,
      })
      .strict(),
    output: z.object({ issues: z.array(forgeIssueSchema).max(100), cursor }).strict(),
  },
  'issues.get': {
    input: z.object(pullRequest).strict(),
    output: z.object({ issue: forgeIssueSchema.nullable() }).strict(),
  },
  'issues.create': {
    input: z
      .object({
        ...repository,
        title: text(1000).min(1),
        body: text(262_144).default(''),
        labels: z.array(text(256)).max(50).optional(),
      })
      .strict(),
    output: z.object({ issue: forgeIssueSchema }).strict(),
  },
  'issues.update': {
    input: z
      .object({
        ...pullRequest,
        title: text(1000).min(1).optional(),
        body: text(262_144).optional(),
        labels: z.array(text(256)).max(50).optional(),
        state: z.literal('open').optional(),
      })
      .strict(),
    output: z.object({ issue: forgeIssueSchema }).strict(),
  },
  'issues.close': {
    input: z.object(pullRequest).strict(),
    output: empty,
  },
  'issueComments.list': {
    input: z.object({ ...pullRequest, ...list }).strict(),
    output: z.object({ comments: z.array(forgeCommentSchema).max(100), cursor }).strict(),
  },
  'issueComments.create': {
    input: z.object({ ...pullRequest, body: text(65_536).min(1) }).strict(),
    output: z.object({ comment: forgeCommentSchema }).strict(),
  },
  'events.parse': {
    /** A webhook delivery as the deployment received it; the provider checks its signature. */
    input: z
      .object({
        headers: z.record(text(128), text(8192)),
        body: text(26_214_400),
      })
      .strict(),
    output: z.object({ events: z.array(forgeEventSchema).max(100) }).strict(),
  },
} as const;
